import { Controller } from '@hotwired/stimulus'
import Swiper from 'swiper'
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules'

// Breakpoints from React code
const SCREENS = {
  MD: 1194,
}

export default class ProductGalleryController extends Controller {
  static targets = ['swiper', 'modal', 'modalImage', 'modalScroll', 'thumbnail']

  declare readonly swiperTarget: HTMLElement
  declare readonly modalTarget: HTMLElement
  declare readonly modalImageTargets: HTMLElement[]
  declare readonly modalScrollTarget: HTMLElement
  declare readonly hasModalScrollTarget: boolean
  declare readonly thumbnailTargets: HTMLElement[]
  declare swiper: Swiper

  connect(): void {
    this.initSwiper()
  }

  disconnect(): void {
    if (this.swiper) {
      this.swiper.destroy()
    }
  }

  initSwiper(): void {
    const slidesCount =
      this.swiperTarget.querySelectorAll('.swiper-slide').length

    this.swiper = new Swiper(this.swiperTarget, {
      modules: [Navigation, Pagination, Scrollbar, A11y],
      spaceBetween: 0,
      slidesPerView: slidesCount >= 2 ? 1.2 : 1,
      observer: true,
      observeParents: true,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      scrollbar: {
        el: '.swiper-scrollbar',
        draggable: true,
        hide: true,
      },
      breakpoints: {
        [SCREENS.MD]: {
          slidesPerView: slidesCount >= 2 ? 2 : 1,
          spaceBetween: 0,
          slidesPerGroup: slidesCount >= 2 ? 2 : 1,
        },
      },
      on: {
        init: (swiper) => {
          // Remove unnecessary aria attributes if needed, mimicking react code
          const wrapper = swiper.el.querySelector('.swiper-wrapper')
          wrapper?.removeAttribute('aria-live')
        },
      },
    })
  }

  openModal(event: Event): void {
    const button = event.currentTarget as HTMLElement
    const index = parseInt(button.dataset.index || '0', 10)

    this.modalTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'

    // Focus the modal so it can receive keyboard events (Escape)
    this.modalTarget.focus()

    // Scroll to the specific image in modal
    this._scrollToImage(index)
  }

  closeModal(): void {
    this.modalTarget.classList.add('hidden')
    document.body.style.overflow = ''
  }

  scrollToImage(event: Event): void {
    const button = event.currentTarget as HTMLElement
    const index = parseInt(button.dataset.index || '0', 10)
    this._scrollToImage(index)
  }

  private _scrollToImage(index: number): void {
    const targetImage =
      this.modalImageTargets[index] ??
      document.getElementById(`zoomed-${index}`)

    if (!targetImage) return

    this._highlightThumbnail(index)

    // Use the dedicated scroll container target when available,
    // otherwise fall back to the modal itself.
    const scrollContainer = this.hasModalScrollTarget
      ? this.modalScrollTarget
      : this.modalTarget

    requestAnimationFrame(() => {
      const containerRect = scrollContainer.getBoundingClientRect()
      const targetRect = targetImage.getBoundingClientRect()
      const offset =
        targetRect.top - containerRect.top + scrollContainer.scrollTop

      scrollContainer.scrollTo({
        top: Math.max(0, offset),
        behavior: 'smooth',
      })
    })
  }

  private _highlightThumbnail(activeIndex: number): void {
    for (const [i, thumb] of this.thumbnailTargets.entries()) {
      if (i === activeIndex) {
        thumb.classList.remove('ring-transparent')
        thumb.classList.add('ring-black')
      } else {
        thumb.classList.remove('ring-black')
        thumb.classList.add('ring-transparent')
      }
    }
  }
}
